import { useEffect, type ComponentType, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { houseLoginUrl } from './shell/houseLogin';
import { AuthProvider } from './contexts/AuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { EntitlementProvider } from './contexts/EntitlementContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { InternalsProvider } from './contexts/InternalsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import GoogleDone from './pages/GoogleDone';
import Dashboard from './pages/Dashboard';
import Launcher from './pages/Launcher';
import Billing from './pages/Billing';
import BeatsHome from './pages/BeatsHome';
import { shellFromHost, type Shell } from './shell/shell';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import Subjects from './pages/Subjects';
import { subjectPathFor } from './pages/projectsRedirect';
import ProjectSettings from './pages/ProjectSettings';
import CategorySettings from './pages/CategorySettings';
import TopicSettings from './pages/TopicSettings';
import TopicPages from './pages/TopicPages';
import PageBuilderWorkflow from './pages/PageBuilderWorkflow';
import PageBuilderSettings from './pages/PageBuilderSettings';
import GitHubCallback from './pages/GitHubCallback';
import Trending from './pages/Trending';
import BoardroomsHome from './pages/BoardroomsHome';
import BoardroomRoom from './pages/BoardroomRoom';
import WatchHome from './pages/WatchHome';
import WatchSubject from './pages/WatchSubject';
import LinkBuilder from './pages/LinkBuilder';
import LinkBuilderWorkspace from './pages/LinkBuilderWorkspace';
import NewsBeats from './pages/NewsBeats';
import NewsBeat from './pages/NewsBeat';
import ManagedSites from './pages/ManagedSites';
import ReviewQueue from './pages/ReviewQueue';
import Artifacts from './pages/Artifacts';
import SubjectWorkspace from './pages/SubjectWorkspace';
import SiteScaffoldStudio from './pages/SiteScaffoldStudio';
import SiteScaffoldPreviewTab from './pages/SiteScaffoldPreviewTab';
import NotFound from './pages/NotFound';

function EditPageRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/page-builder/${id}`} replace />;
}

/**
 * Deployment targets have moved OUT of Settings and into Deliver. They spent a
 * while as `Settings ▸ Page Generation ▸ Deployment Targets`, which framed
 * shipping as configuration — you set up a deploy target the way you set an API
 * key — and buried it three clicks below the pages it publishes.
 *
 * Both old shapes still resolve, `?site=…&plan=…` intact, so saved links keep
 * landing on the same site and plan.
 */
function ManagedSitesRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/deliver${search}`} replace />;
}

/**
 * `/topic-pages` -> `/pages`. Renamed when it was promoted into the nav: topic
 * is one optional filter on it, not its scope. Query params carry over so the
 * Dashboard's `?status=` tiles and any saved links keep landing correctly.
 */
function ProjectsRedirect() {
  const [params] = useSearchParams();
  return <Navigate to={subjectPathFor(params)} replace />;
}

/**
 * What `/` is depends on which app you are in.
 *
 * EVERY SHELL USED TO GET `Dashboard`, and Dashboard is the PUBLISH dashboard —
 * Pending Recommendations, Top Growing Keywords, Keyword Coverage. So a News
 * Beats customer's first screen was about keyword coverage and linked out to
 * Trends, a product they had not bought: the exact bleed the shell split exists
 * to end, sitting on the first thing they see.
 *
 * Each product answers its own two questions now — what needs me, and what has
 * it found — in its own vocabulary. Deliberately NOT one shared stat strip
 * scoped per product: a briefing is not a draft you finish and a room is not a
 * page awaiting review, and forcing one vocabulary across three products is the
 * same mistake as one nav, one screen lower down.
 */
const HOME_BY_SHELL: Record<Shell, ComponentType> = {
  app: Launcher,
  beats: BeatsHome,
  publish: Dashboard,
  // RECON'S HOME IS THE ROOM LIST, at one address: `/` and `/boardroom` were
  // the same screen twice, and only one lit up in the menu.
  boardroom: RoomsAtTheirAddress,
};

function RoomsAtTheirAddress() {
  return <Navigate to="/boardroom" replace />;
}

function Home() {
  // `shellFromHost` already falls back to `app` for anything unrecognised, so
  // this cannot miss.
  const Screen = HOME_BY_SHELL[shellFromHost(window.location.hostname)] ?? Launcher;
  return <Screen />;
}

function TopicPagesRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/pages${search}`} replace />;
}

/**
 * Login and registration live in the house. A `/login` reached on a product
 * host — a bookmark, a typed URL — goes to `app.<domain>/login` with this host
 * as the place to come back to, so there is one sign-in screen rather than one
 * per product. See `shell/houseLogin.ts`.
 */
function HouseOnly({ children }: { children: ReactNode }) {
  const bounce = houseLoginUrl(
    window.location.hostname,
    window.location.origin,
    `${window.location.origin}/`,
  );
  useEffect(() => {
    if (bounce) window.location.replace(bounce);
  }, [bounce]);
  if (bounce) return null;
  return <>{children}</>;
}

function App() {
  return (
    <AuthProvider>
      {/* Inside AuthProvider because the reveal is gated on is_admin, and above
          the router so the chord works on every route. */}
      <InternalsProvider>
      <WebSocketProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<HouseOnly><Login /></HouseOnly>} />
          <Route path="/register" element={<HouseOnly><Register /></HouseOnly>} />
          {/* The rest of the signed-out surface. Emailed links point at the
              house (FRONTEND_URL), and the Google callback sends its handoff
              here, so all of these are house-only like /login. */}
          <Route path="/forgot-password" element={<HouseOnly><ForgotPassword /></HouseOnly>} />
          <Route path="/reset-password" element={<HouseOnly><ResetPassword /></HouseOnly>} />
          <Route path="/verify-email" element={<HouseOnly><VerifyEmail /></HouseOnly>} />
          <Route path="/auth/google/done" element={<HouseOnly><GoogleDone /></HouseOnly>} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                {/* Inside ProtectedRoute: entitlements are a property of a
                    signed-in user, so fetching them before login would be a
                    guaranteed 401. */}
                <EntitlementProvider>
                  <ProjectProvider>
                    <Layout />
                  </ProjectProvider>
                </EntitlementProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<Home />} />
            {/* The four-level drill-down is gone; its URLs are not. */}
            <Route path="projects" element={<ProjectsRedirect />} />
            <Route path="subjects" element={<Subjects />} />
            <Route path="projects/:projectId/settings" element={<ProjectSettings />} />
            <Route path="projects/:projectId/settings/:section" element={<ProjectSettings />} />
            <Route path="categories/:categoryId/settings" element={<CategorySettings />} />
            <Route path="categories/:categoryId/settings/:section" element={<CategorySettings />} />
            <Route path="topics/:topicId/settings" element={<TopicSettings />} />
            <Route path="topics/:topicId/settings/:section" element={<TopicSettings />} />
            <Route path="settings" element={<Settings />} />
            {/* Your own profile — reached from the username in the top-right,
                not from Settings (which is app config). */}
            <Route path="account" element={<Profile />} />
            {/* Credits and plans. Shared (not shell-scoped) so a link from any
                product resolves; the drawer sends everyone to the house copy. */}
            <Route path="billing" element={<Billing />} />
            {/* <Route path="topic-keywords" element={<Navigate to="/projects" replace />} /> */}
            <Route path="managed-sites" element={<ManagedSitesRedirect />} />
            {/* Two hops, and the first one used to be a bare string — which
                threw the query away before ManagedSitesRedirect could preserve
                it, so a bookmarked `?site=3&plan=7` landed on /deliver with
                nothing selected. One hop, carrying the search. */}
            <Route path="managed-pages" element={<ManagedSitesRedirect />} />
            {/* The cross-cutting page browser: every page, filterable by
                deployment plan / status / site / project. Batch work on pages
                is application work, so it lives at the top level rather than
                under Settings, where only site + plan CRUD belong.
                `/topic-pages` was its old, topic-implying name. */}
            <Route path="pages" element={<TopicPages />} />
            {/* One shelf for everything produced, whatever made it. */}
            <Route path="artifacts" element={<Artifacts />} />
            {/* The subject workspace, alongside /projects rather than replacing
                it — the two get compared before the drill-down is retired. */}
            <Route path="subjects/:projectId" element={<SubjectWorkspace />} />
            {/* Deliver — publishing is a stage of the work, not a setting. */}
            <Route path="deliver" element={<ManagedSites />} />
            {/* Read what is about to go live. A route of its own rather
                than a tab: it is worked through page by page, spans every
                site, and wants the whole viewport. */}
            <Route path="deliver/review" element={<ReviewQueue />} />
            {/* The site designer is a full-screen two-pane workspace, so it gets
                its own route like link-builder/:mapId rather than another query
                param on the already-1300-line Deliver page. */}
            <Route path="deliver/:siteId/design" element={<SiteScaffoldStudio />} />
            {/* The same preview at full width, in its own tab. A ROUTE and not
                a blob: the preview endpoint is authenticated, so a blob tab had
                no session to fetch the next page with and no parent to hand a
                click to — it could be looked at and not read. */}
            <Route
              path="deliver/:siteId/design/preview"
              element={<SiteScaffoldPreviewTab />}
            />
            <Route path="topic-pages" element={<TopicPagesRedirect />} />
            <Route path="edit-page/:id" element={<EditPageRedirect />} />
            <Route path="page-builder/:pageId" element={<PageBuilderWorkflow />} />
            <Route path="page-builder/:pageId/settings" element={<PageBuilderSettings />} />
            <Route path="page-builder/:pageId/settings/:section" element={<PageBuilderSettings />} />
            <Route path="recommendations" element={<Navigate to="/trending?recommendations_only=true" replace />} />
            {/* Two ROUTES, one component. The tab used to be `?tab=`, which no
                nav entry can be highlighted by — `navMatches` reads the
                pathname. Both render `Trending`, which picks its table from the
                path it was reached at. */}
            <Route path="trending" element={<Trending />} />
            <Route path="trending/keywords" element={<Trending />} />
            {/* `/brainstorm` and `/brainstorm/:sessionId` were here; boardroom
                replaced that product and its routes went with it. */}
            {/* ONE ROOM LIST, here and at Home: the two used to be different
                lists of the same rooms. */}
            <Route path="boardroom" element={<BoardroomsHome />} />
            <Route path="boardroom/:boardroomId" element={<BoardroomRoom />} />
            {/* Watch is served from the boardroom shell (see shell/routeShell.ts) but has
                its own paths: a watched subject outlives the room that created it, so it
                cannot live under /boardroom/:id. */}
            <Route path="watch" element={<WatchHome />} />
            <Route path="watch/subjects/:subjectId" element={<WatchSubject />} />
            <Route path="link-builder" element={<LinkBuilder />} />
            <Route path="link-builder/:mapId" element={<LinkBuilderWorkspace />} />
            <Route path="news-beats" element={<NewsBeats />} />
            <Route path="news-beats/:beatId" element={<NewsBeat />} />
            <Route path="news-beats/:beatId/:section" element={<NewsBeat />} />
            {/* User Approvals now lives at Settings ▸ Users (admin-only). */}
            <Route path="admin/users" element={<Navigate to="/settings?tab=users" replace />} />
            {/* Site Parsers lives at Settings ▸ Network ▸ Parsers, which is the
                only place anything links to. Standing alone here it belonged to
                no stage, so nothing in the nav highlighted and it had no way
                back — a dead end reachable only by typing the URL. Redirected
                rather than deleted so any bookmark still lands somewhere. */}
            <Route
              path="admin/site-parsers"
              element={<Navigate to="/settings?tab=network&sub=parsers" replace />}
            />
            <Route path="profile" element={<Navigate to="/account" replace />} />
            <Route path="settings/github" element={<Navigate to="/settings?tab=connections&sub=github" replace />} />
            <Route path="github/callback" element={<GitHubCallback />} />
            {/* LAST, and inside the layout route on purpose. Without a
                catch-all an unmatched URL matched nothing and React Router
                rendered null: a white screen with no nav, which reads as a
                crash rather than as a wrong address. */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </WebSocketProvider>
      </InternalsProvider>
    </AuthProvider>
  );
}

export default App;
